import { CalculatorLoading } from "@/components/meeting-waste/calculator-loading"
import { CalculatorResult } from "@/components/meeting-waste/calculator-result"
import { Button } from "@/components/ui/button"
import { Card } from "@/components/ui/card"
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible"
import { Label } from "@/components/ui/label"
import { Slider } from "@/components/ui/slider"
import { AnimatePresence, MotionDiv, MotionH1, MotionP } from "@/lib/framer"
import { calculateAnnualWaste } from "@/lib/meeting-waste/calculations"
import { asyncStandupPath, dailyStandupCostPath } from "@/routes"
import { trackRybbitEvent } from "@/lib/rybbit"
import { Link } from "@inertiajs/react"
import { ArrowRight, Calculator, ChevronDown, TrendingUp } from "lucide-react"
import { useMemo, useState } from "react"

function readSearchParam(key: string): string | null {
  if (typeof window === "undefined") return null
  return new URLSearchParams(window.location.search).get(key)
}

function writeAmountToUrl(amount: string | null) {
  if (typeof window === "undefined") return
  const url = new URL(window.location.href)
  if (amount) url.searchParams.set("amount", amount)
  else url.searchParams.delete("amount")
  url.searchParams.delete("utm_source")
  url.searchParams.delete("utm_campaign")
  window.history.replaceState({}, "", `${url.pathname}${url.search}${url.hash}`)
}

const SLIDER_VALUES = {
  TEAMS: {
    MIN: 1,
    MAX: 20,
    STEP: 1,
  },
  TEAM_SIZE: {
    MIN: 2,
    MAX: 20,
    STEP: 1,
  },
  MEETINGS_PER_WEEK: {
    MIN: 1,
    MAX: 52,
    STEP: 1,
  },
  MINUTES_PER_MEETING: {
    MIN: 5,
    MAX: 90,
    STEP: 5,
  },
  HOURLY_RATE: {
    MIN: 10,
    MAX: 1000,
    STEP: 5,
  },
  ATTENDANCE_RATE: {
    MIN: 0.5,
    MAX: 1,
    STEP: 0.05,
  },
  OVERRUN_FACTOR: {
    MIN: 1,
    MAX: 2,
    STEP: 0.1,
  },
};

const SLIDER_KEY_MAP: Record<keyof Omit<CalculatorInputs, "opportunityCostMultiplier">, keyof typeof SLIDER_VALUES> = {
  numberOfTeams: "TEAMS",
  avgTeamSize: "TEAM_SIZE",
  meetingsPerWeek: "MEETINGS_PER_WEEK",
  minutesPerMeeting: "MINUTES_PER_MEETING",
  hourlyRate: "HOURLY_RATE",
  attendanceRate: "ATTENDANCE_RATE",
  overrunFactor: "OVERRUN_FACTOR",
};

interface CalculatorInputs {
  numberOfTeams: number;
  avgTeamSize: number;
  meetingsPerWeek: number;
  minutesPerMeeting: number;
  hourlyRate: number;
  // Advanced options
  attendanceRate: number;
  overrunFactor: number;
  opportunityCostMultiplier: number;
}

const DEFAULT_VALUES: CalculatorInputs = {
  numberOfTeams: 1,
  avgTeamSize: 5,
  meetingsPerWeek: 5,
  minutesPerMeeting: 15,
  hourlyRate: 100,
  attendanceRate: 1,
  overrunFactor: 1.2,
  opportunityCostMultiplier: 1.0,
};

export function MeetingWasteCalculator() {
  const [inputs, setInputs] = useState<CalculatorInputs>(DEFAULT_VALUES)
  const [isCalculating, setIsCalculating] = useState(false)
  const [amount, setAmountState] = useState<string | null>(() => readSearchParam("amount"))
  const [utmSource] = useState(() => readSearchParam("utm_source"))
  const [utmCampaign] = useState(() => readSearchParam("utm_campaign"))

  const [formValues, setFormValues] = useState<Record<keyof CalculatorInputs, string>>(
    Object.fromEntries(Object.entries(DEFAULT_VALUES).map(([key, value]) => [key, String(value)])) as Record<
      keyof CalculatorInputs,
      string
    >,
  )

  const isValid = useMemo(() => {
    return Object.entries(formValues).every(([key, value]) => {
      const fieldName = key as keyof CalculatorInputs;
      if (fieldName === "opportunityCostMultiplier") return true; // Not in form
      if (value.trim() === "") return false;
      const num = parseFloat(value);
      if (isNaN(num)) return false;

      const sliderKey = SLIDER_KEY_MAP[fieldName as keyof typeof SLIDER_KEY_MAP];
      const constraints = SLIDER_VALUES[sliderKey];
      return num >= constraints.MIN && num <= constraints.MAX;
    });
  }, [formValues]);

  const result = amount ? parseInt(amount) : null;
  const [showAdvanced, setShowAdvanced] = useState(false);

  const handleCalculate = async () => {
    setIsCalculating(true);

    trackRybbitEvent("calculate_meeting_waste", {
      teams: inputs.numberOfTeams,
      team_size: inputs.avgTeamSize,
      hourly_rate: inputs.hourlyRate,
      meetings_per_week: inputs.meetingsPerWeek,
      minutes_per_meeting: inputs.minutesPerMeeting,
    })

    // Simulate calculation delay for dramatic effect
    setTimeout(() => {
      const waste = calculateAnnualWaste(inputs)
      const wasteAmount = waste.toString()
      setAmountState(wasteAmount)
      writeAmountToUrl(wasteAmount)
      setIsCalculating(false)

      trackRybbitEvent("meeting_waste_calculated", {
        annual_waste: waste,
        waste_range: waste < 50000 ? "low" : waste < 250000 ? "medium" : waste < 1000000 ? "high" : "extreme",
        utm_source: utmSource ?? "direct",
        utm_campaign: utmCampaign ?? "meeting-waste",
      })
    }, 5000)
  }

  const handleInputChange = (fieldName: keyof CalculatorInputs, value: string) => {
    setFormValues((prev) => ({ ...prev, [fieldName]: value }));
  };

  const handleInputBlur = (fieldName: keyof CalculatorInputs, value: string) => {
    const sliderKey = SLIDER_KEY_MAP[fieldName as keyof typeof SLIDER_KEY_MAP];
    const constraints = SLIDER_VALUES[sliderKey];
    let num = parseFloat(value);

    if (isNaN(num) || value.trim() === "") {
      num = inputs[fieldName]; // Reset to last valid number from main state
    }

    const clampedValue = Math.max(constraints.MIN, Math.min(constraints.MAX, num));

    setFormValues((prev) => ({ ...prev, [fieldName]: String(clampedValue) }));
    setInputs((prev) => ({ ...prev, [fieldName]: clampedValue }));
  };

  return (
    <div className="mx-auto max-w-4xl space-y-8">
      {/* Hero Section with Animations */}
      <MotionDiv
        className="space-y-4 text-center"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <MotionP
          className="text-golden-500 text-sm font-semibold tracking-wider uppercase"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.9, duration: 0.5 }}
        >
          A <span className="text-foreground">Git</span> Digest Tool
        </MotionP>
        <MotionH1
          className="text-foreground text-5xl/[1.1] font-bold tracking-tight text-balance sm:text-6xl/[1.1] md:text-7xl/[1.1]"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1, duration: 0.5 }}
        >
          Wasted Money{" "}
          <span className="decoration-golden-500 underline decoration-4 underline-offset-4">Meeting Calculator</span>
        </MotionH1>
        <MotionP
          className="text-muted-foreground relative mx-auto max-w-2xl text-xl/7 font-medium"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.2, duration: 0.5 }}
        >
          See the money and time a company wastes in meetings: salary × attendees × duration.
          <br />
          That annual figure is the standup tax.
        </MotionP>
        <MotionP
          className="text-muted-foreground mx-auto max-w-2xl text-base"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.25, duration: 0.5 }}
        >
          <Link
            href={dailyStandupCostPath()}
            className="text-foreground decoration-golden-500 underline underline-offset-4"
          >
            Daily standup cost
          </Link>{" "}
          by salary and team size, or the{" "}
          <Link
            href={asyncStandupPath()}
            className="text-foreground decoration-golden-500 underline underline-offset-4"
          >
            async standup
          </Link>{" "}
          playbook.
        </MotionP>
      </MotionDiv>

      {/* Calculator Card with stagger animations */}
      <AnimatePresence mode="wait">
        {isCalculating ? (
          <MotionDiv key="loading">
            <CalculatorLoading />
          </MotionDiv>
        ) : result === null ? (
          <MotionDiv
            key="form"
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3, duration: 0.5 }}
          >
            <Card className="border-muted bg-card/50 p-6 backdrop-blur-sm md:p-8">
              <div className="space-y-6">
                {/* Team Configuration */}
                <MotionDiv
                  className="grid gap-6 md:grid-cols-2"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.4, duration: 0.5 }}
                >
                  <div className="space-y-2">
                    <Label htmlFor="teams" className="flex items-center gap-2">
                      <TrendingUp className="h-4 w-4" />
                      Number of Teams
                    </Label>
                    <div className="flex items-center justify-between">
                      <Slider
                        id="teams"
                        min={SLIDER_VALUES.TEAMS.MIN}
                        max={SLIDER_VALUES.TEAMS.MAX}
                        step={SLIDER_VALUES.TEAMS.STEP}
                        value={[inputs.numberOfTeams]}
                        onValueChange={(value) => {
                          const newValue = value[0];
                          setInputs({ ...inputs, numberOfTeams: newValue });
                          setFormValues({ ...formValues, numberOfTeams: String(newValue) });
                        }}
                        className="w-[calc(100%-3rem)] flex-1"
                      />
                      <div className="ml-2 flex min-w-[2.5rem] items-center gap-1">
                        <MotionDiv
                          className="hidden text-right font-mono text-lg font-bold sm:block"
                          key={inputs.numberOfTeams}
                          initial={{ scale: 1.2 }}
                          animate={{ scale: 1 }}
                          transition={{ type: "spring", stiffness: 300 }}
                        >
                          {inputs.numberOfTeams}
                        </MotionDiv>
                        {/* Inline input for small screens */}
                        <input
                          type="number"
                          min={SLIDER_VALUES.TEAMS.MIN}
                          max={SLIDER_VALUES.TEAMS.MAX}
                          step={SLIDER_VALUES.TEAMS.STEP}
                          value={formValues.numberOfTeams}
                          onFocus={(e) => e.target.select()}
                          onChange={(e) => handleInputChange("numberOfTeams", e.target.value)}
                          onBlur={(e) => handleInputBlur("numberOfTeams", e.target.value)}
                          className="border-input bg-background focus:ring-golden-500 block w-12 rounded border px-1 py-0.5 text-center font-mono text-sm focus:ring-2 focus:outline-none sm:hidden"
                          aria-label="Number of Teams"
                        />
                      </div>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="team-size" className="flex items-center gap-2">
                      <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"
                        />
                      </svg>
                      Attendees per meeting
                    </Label>
                    <div className="flex items-center justify-between">
                      <Slider
                        id="team-size"
                        min={SLIDER_VALUES.TEAM_SIZE.MIN}
                        max={SLIDER_VALUES.TEAM_SIZE.MAX}
                        step={SLIDER_VALUES.TEAM_SIZE.STEP}
                        value={[inputs.avgTeamSize]}
                        onValueChange={(value) => {
                          const newValue = value[0];
                          setInputs({ ...inputs, avgTeamSize: newValue });
                          setFormValues({ ...formValues, avgTeamSize: String(newValue) });
                        }}
                        className="w-[calc(100%-3rem)] flex-1"
                      />
                      <div className="ml-2 flex min-w-[2.5rem] items-center gap-1">
                        <MotionDiv
                          className="hidden text-right font-mono text-lg font-bold sm:block"
                          key={inputs.avgTeamSize}
                          initial={{ scale: 1.2 }}
                          animate={{ scale: 1 }}
                          transition={{ type: "spring", stiffness: 300 }}
                        >
                          {inputs.avgTeamSize}
                        </MotionDiv>
                        {/* Inline input for small screens */}
                        <input
                          type="number"
                          min={SLIDER_VALUES.TEAM_SIZE.MIN}
                          max={SLIDER_VALUES.TEAM_SIZE.MAX}
                          step={SLIDER_VALUES.TEAM_SIZE.STEP}
                          value={formValues.avgTeamSize}
                          onFocus={(e) => e.target.select()}
                          onChange={(e) => handleInputChange("avgTeamSize", e.target.value)}
                          onBlur={(e) => handleInputBlur("avgTeamSize", e.target.value)}
                          className="border-input bg-background focus:ring-golden-500 block w-12 rounded border px-1 py-0.5 text-center font-mono text-sm focus:ring-2 focus:outline-none sm:hidden"
                          aria-label="Attendees per meeting"
                        />
                      </div>
                    </div>
                  </div>
                </MotionDiv>

                {/* Meeting Configuration */}
                <MotionDiv
                  className="grid gap-6 md:grid-cols-2"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.5, duration: 0.5 }}
                >
                  <div className="space-y-2">
                    <Label htmlFor="frequency" className="flex items-center gap-2">
                      <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                        />
                      </svg>
                      Meetings Per Week
                    </Label>
                    <div className="flex items-center justify-between">
                      <Slider
                        id="frequency"
                        min={SLIDER_VALUES.MEETINGS_PER_WEEK.MIN}
                        max={SLIDER_VALUES.MEETINGS_PER_WEEK.MAX}
                        step={SLIDER_VALUES.MEETINGS_PER_WEEK.STEP}
                        value={[inputs.meetingsPerWeek]}
                        onValueChange={(value) => {
                          const newValue = value[0];
                          setInputs({ ...inputs, meetingsPerWeek: newValue });
                          setFormValues({ ...formValues, meetingsPerWeek: String(newValue) });
                        }}
                        className="w-[calc(100%-3rem)] flex-1"
                      />
                      <div className="ml-2 flex min-w-[2.5rem] items-center gap-1">
                        <MotionDiv
                          className="hidden text-right font-mono text-lg font-bold sm:block"
                          key={inputs.meetingsPerWeek}
                          initial={{ scale: 1.2 }}
                          animate={{ scale: 1 }}
                          transition={{ type: "spring", stiffness: 300 }}
                        >
                          {inputs.meetingsPerWeek}
                        </MotionDiv>
                        {/* Inline input for small screens */}
                        <input
                          type="number"
                          min={SLIDER_VALUES.MEETINGS_PER_WEEK.MIN}
                          max={SLIDER_VALUES.MEETINGS_PER_WEEK.MAX}
                          step={SLIDER_VALUES.MEETINGS_PER_WEEK.STEP}
                          value={formValues.meetingsPerWeek}
                          onFocus={(e) => e.target.select()}
                          onChange={(e) => handleInputChange("meetingsPerWeek", e.target.value)}
                          onBlur={(e) => handleInputBlur("meetingsPerWeek", e.target.value)}
                          className="border-input bg-background focus:ring-golden-500 block w-12 rounded border px-1 py-0.5 text-center font-mono text-sm focus:ring-2 focus:outline-none sm:hidden"
                          aria-label="Meetings Per Week"
                        />
                      </div>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="duration" className="flex items-center gap-2">
                      <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                        />
                      </svg>
                      Minutes Per Meeting
                    </Label>
                    <div className="flex items-center justify-between">
                      <Slider
                        id="duration"
                        min={SLIDER_VALUES.MINUTES_PER_MEETING.MIN}
                        max={SLIDER_VALUES.MINUTES_PER_MEETING.MAX}
                        step={SLIDER_VALUES.MINUTES_PER_MEETING.STEP}
                        value={[inputs.minutesPerMeeting]}
                        onValueChange={(value) => {
                          const newValue = value[0];
                          setInputs({ ...inputs, minutesPerMeeting: newValue });
                          setFormValues({ ...formValues, minutesPerMeeting: String(newValue) });
                        }}
                        className="w-[calc(100%-3rem)] flex-1"
                      />
                      <div className="ml-2 flex min-w-[2.5rem] items-center gap-1">
                        <MotionDiv
                          className="hidden text-right font-mono text-lg font-bold sm:block"
                          key={inputs.minutesPerMeeting}
                          initial={{ scale: 1.2 }}
                          animate={{ scale: 1 }}
                          transition={{ type: "spring", stiffness: 300 }}
                        >
                          {inputs.minutesPerMeeting}
                        </MotionDiv>
                        {/* Inline input for small screens */}
                        <input
                          type="number"
                          min={SLIDER_VALUES.MINUTES_PER_MEETING.MIN}
                          max={SLIDER_VALUES.MINUTES_PER_MEETING.MAX}
                          step={SLIDER_VALUES.MINUTES_PER_MEETING.STEP}
                          value={formValues.minutesPerMeeting}
                          onFocus={(e) => e.target.select()}
                          onChange={(e) => handleInputChange("minutesPerMeeting", e.target.value)}
                          onBlur={(e) => handleInputBlur("minutesPerMeeting", e.target.value)}
                          className="border-input bg-background focus:ring-golden-500 block w-12 rounded border px-1 py-0.5 text-center font-mono text-sm focus:ring-2 focus:outline-none sm:hidden"
                          aria-label="Minutes Per Meeting"
                        />
                      </div>
                    </div>
                  </div>
                </MotionDiv>

                {/* Cost Configuration */}
                <MotionDiv
                  className="space-y-2"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.6, duration: 0.5 }}
                >
                  <Label htmlFor="rate" className="flex items-center gap-2">
                    <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                      />
                    </svg>
                    Fully-loaded hourly salary (USD)
                  </Label>
                  <div className="flex items-center justify-between">
                    <Slider
                      id="rate"
                      min={SLIDER_VALUES.HOURLY_RATE.MIN}
                      max={SLIDER_VALUES.HOURLY_RATE.MAX}
                      step={SLIDER_VALUES.HOURLY_RATE.STEP}
                      value={[inputs.hourlyRate]}
                      onValueChange={(value) => {
                        const newValue = value[0];
                        setInputs({ ...inputs, hourlyRate: newValue });
                        setFormValues({ ...formValues, hourlyRate: String(newValue) });
                      }}
                      className="w-[calc(100%-3.5rem)] flex-1"
                    />
                    <div className="ml-2 flex min-w-[3rem] items-center gap-1">
                      <MotionDiv
                        className="hidden text-right font-mono text-lg font-bold sm:block"
                        key={inputs.hourlyRate}
                        initial={{ scale: 1.2 }}
                        animate={{ scale: 1 }}
                        transition={{ type: "spring", stiffness: 300 }}
                      >
                        ${inputs.hourlyRate}
                      </MotionDiv>
                      {/* Inline input for small screens */}
                      <input
                        type="number"
                        min={SLIDER_VALUES.HOURLY_RATE.MIN}
                        max={SLIDER_VALUES.HOURLY_RATE.MAX}
                        step={SLIDER_VALUES.HOURLY_RATE.STEP}
                        value={formValues.hourlyRate}
                        onFocus={(e) => e.target.select()}
                        onChange={(e) => handleInputChange("hourlyRate", e.target.value)}
                        onBlur={(e) => handleInputBlur("hourlyRate", e.target.value)}
                        className="border-input bg-background focus:ring-golden-500 block w-16 rounded border px-1 py-0.5 text-center font-mono text-sm focus:ring-2 focus:outline-none sm:hidden"
                        aria-label="Hourly Rate"
                      />
                    </div>
                  </div>
                </MotionDiv>

                {/* Advanced Options */}
                <MotionDiv initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.7, duration: 0.5 }}>
                  <Collapsible open={showAdvanced} onOpenChange={setShowAdvanced}>
                    <CollapsibleTrigger asChild>
                      <Button variant="ghost" className="group w-full">
                        <span className="flex items-center gap-2 text-sm">
                          {showAdvanced ? "Hide" : "Show"} Nerdy Details 🤓
                          <ChevronDown className={`h-4 w-4 transition-transform ${showAdvanced ? "rotate-180" : ""}`} />
                        </span>
                      </Button>
                    </CollapsibleTrigger>
                    <CollapsibleContent className="space-y-4 pt-4">
                      <div className="bg-muted/50 overflow-x-auto rounded-lg p-4 font-mono text-sm">
                        <code className="block text-[10px] whitespace-pre-wrap sm:text-xs sm:whitespace-pre">
                          {`Annual Waste $ = (#Teams x TeamSize) x MeetingsPerWeek x (MinutesPerMeeting ÷ 60) x 48 weeks x HourlyCost x AttendanceRate x OverrunFactor`}
                        </code>
                      </div>

                      <div className="grid gap-4 sm:grid-cols-2">
                        <div className="space-y-2">
                          <Label htmlFor="attendance" className="flex items-center justify-between">
                            <span>Attendance Rate</span>
                            <span className="text-muted-foreground text-sm">
                              {(inputs.attendanceRate * 100).toFixed(0)}%
                            </span>
                          </Label>
                          <div className="flex items-center gap-1">
                            <Slider
                              id="attendance"
                              min={SLIDER_VALUES.ATTENDANCE_RATE.MIN}
                              max={SLIDER_VALUES.ATTENDANCE_RATE.MAX}
                              step={SLIDER_VALUES.ATTENDANCE_RATE.STEP}
                              value={[inputs.attendanceRate]}
                              onValueChange={(value) => {
                                const newValue = value[0];
                                setInputs({ ...inputs, attendanceRate: newValue });
                                setFormValues({ ...formValues, attendanceRate: String(newValue) });
                              }}
                              className="flex-1"
                            />
                            {/* Inline input for small screens */}
                            <input
                              type="number"
                              min={SLIDER_VALUES.ATTENDANCE_RATE.MIN}
                              max={SLIDER_VALUES.ATTENDANCE_RATE.MAX}
                              step={SLIDER_VALUES.ATTENDANCE_RATE.STEP}
                              value={formValues.attendanceRate}
                              onFocus={(e) => e.target.select()}
                              onChange={(e) => handleInputChange("attendanceRate", e.target.value)}
                              onBlur={(e) => handleInputBlur("attendanceRate", e.target.value)}
                              className="border-input bg-background focus:ring-golden-500 block w-14 rounded border px-1 py-0.5 text-center font-mono text-sm focus:ring-2 focus:outline-none sm:hidden"
                              aria-label="Attendance Rate"
                            />
                          </div>
                        </div>

                        <div className="space-y-2">
                          <Label htmlFor="overrun" className="flex items-center justify-between">
                            <span>Overrun Factor</span>
                            <span className="text-muted-foreground text-sm">{inputs.overrunFactor}x</span>
                          </Label>
                          <div className="flex items-center gap-1">
                            <Slider
                              id="overrun"
                              min={SLIDER_VALUES.OVERRUN_FACTOR.MIN}
                              max={SLIDER_VALUES.OVERRUN_FACTOR.MAX}
                              step={SLIDER_VALUES.OVERRUN_FACTOR.STEP}
                              value={[inputs.overrunFactor]}
                              onValueChange={(value) => {
                                const newValue = value[0];
                                setInputs({ ...inputs, overrunFactor: newValue });
                                setFormValues({ ...formValues, overrunFactor: String(newValue) });
                              }}
                              className="flex-1"
                            />
                            {/* Inline input for small screens */}
                            <input
                              type="number"
                              min={SLIDER_VALUES.OVERRUN_FACTOR.MIN}
                              max={SLIDER_VALUES.OVERRUN_FACTOR.MAX}
                              step={SLIDER_VALUES.OVERRUN_FACTOR.STEP}
                              value={formValues.overrunFactor}
                              onFocus={(e) => e.target.select()}
                              onChange={(e) => handleInputChange("overrunFactor", e.target.value)}
                              onBlur={(e) => handleInputBlur("overrunFactor", e.target.value)}
                              className="border-input bg-background focus:ring-golden-500 block w-14 rounded border px-1 py-0.5 text-center font-mono text-sm focus:ring-2 focus:outline-none sm:hidden"
                              aria-label="Overrun Factor"
                            />
                          </div>
                        </div>
                      </div>
                    </CollapsibleContent>
                  </Collapsible>
                </MotionDiv>

                {/* Calculate Button */}
                <MotionDiv
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.8, duration: 0.5 }}
                >
                  <Button
                    size="lg"
                    className="group relative w-full overflow-hidden font-semibold"
                    onClick={handleCalculate}
                    disabled={isCalculating || !isValid}
                  >
                    <span className="relative z-10 flex items-center justify-center gap-2">
                      {isCalculating ? (
                        <>
                          <div className="border-background h-4 w-4 animate-spin rounded-full border-2 border-t-transparent" />
                          Calculating wasted money...
                        </>
                      ) : (
                        <>
                          <Calculator className="h-4 w-4" />
                          Calculate Wasted Money
                          <ArrowRight className="ml-2 h-4 w-4" />
                        </>
                      )}
                    </span>
                    <div className="from-golden-500 to-golden-700 absolute inset-0 -z-10 bg-gradient-to-r opacity-0 transition-opacity group-hover:opacity-100" />
                  </Button>
                </MotionDiv>
              </div>
            </Card>
          </MotionDiv>
        ) : (
          <MotionDiv initial={{ opacity: 0, y: 40 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
            <CalculatorResult amount={result} inputs={inputs} />
            <MotionDiv initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
              <Card className="via-primary/5 dark:from-primary/10 dark:via-primary/5 relative mt-8 overflow-hidden border-2 border-orange-200/50 bg-gradient-to-br from-orange-50 to-orange-100/30 p-8 text-center shadow-lg dark:border-orange-800/30 dark:to-orange-500/10">
                {/* Animated background gradient */}
                <div className="absolute inset-0 -z-10">
                  <div className="via-primary/10 dark:from-primary/5 absolute inset-0 animate-pulse bg-gradient-to-br from-orange-100/40 to-orange-200/40 dark:to-orange-500/5" />
                </div>

                <h3 className="mb-2 text-2xl text-gray-900 dark:text-white">
                  Replace that standup tax with GitHub & Slack digests
                </h3>
                <Button
                  asChild
                  size="lg"
                  className="group bg-golden-500 hover:bg-golden-600 relative overflow-hidden font-semibold text-black shadow-lg hover:shadow-xl"
                >
                  <Link href="/sign_in?utm_source=meeting-waste&utm_campaign=calculator">
                    <span className="relative z-10 flex items-center">
                      <span className="hidden sm:inline">Connect GitHub & Subscribe</span>
                      <span className="sm:hidden">Get Started</span>
                      <ArrowRight className="ml-2 size-4 shrink-0" />
                    </span>
                  </Link>
                </Button>
                <p className="dark:text-muted-foreground mt-4 text-xs text-gray-600">
                  *Assumes ${inputs.hourlyRate || 150}/hr fully-loaded cost. Your mileage may vary, but the waste is
                  real.
                </p>
              </Card>
            </MotionDiv>
          </MotionDiv>
        )}
      </AnimatePresence>
    </div>
  );
}
